import { RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { useBrandOffersAdminAuth } from '@/contexts/BrandOffersAdminAuthContext';
import { BRAND_OFFERS_PAGE_PATH } from '@/pages/BrandOffersPage/BrandOffersPage.constants';
import { ADMIN_LOGIN_PATH } from '@/routes/AdminRoutes';
import styles from './AdminLoginPage.module.css';
import type { AdminLoginLocationState } from './AdminLoginPage.types';

const AdminLoginPage = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signed, login } = useBrandOffersAdminAuth();
  const redirectTo = useMemo((): string => {
    const from = (location.state as AdminLoginLocationState | null)?.from;
    if (from && from !== ADMIN_LOGIN_PATH) return from;
    return BRAND_OFFERS_PAGE_PATH;
  }, [location.state]);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (signed) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo, signed]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError('');

    const u = username.trim();
    if (!u || !password) {
      setError('Enter username and password.');
      return;
    }

    setIsSubmitting(true);
    try {
      await login({ username: u, password });
      navigate(redirectTo, { replace: true });
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Could not sign in. Check your credentials and try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h1 className={styles.title}>Brand Offer Admin</h1>
          <p className={styles.subtitle}>
            Sign in to manage brand and global offers, scheduling, and
            visibility.
          </p>
        </div>
        <form className={styles.form} onSubmit={(e) => void onSubmit(e)}>
          <label className={styles.label} htmlFor="brand-offers-admin-user">
            Username
            <input
              autoComplete="username"
              className={styles.input}
              disabled={isSubmitting}
              id="brand-offers-admin-user"
              name="username"
              placeholder="your.name"
              spellCheck={false}
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </label>

          <label className={styles.label} htmlFor="brand-offers-admin-pass">
            Password
            <span className={styles.passwordRow}>
              <input
                autoComplete="current-password"
                className={styles.input}
                disabled={isSubmitting}
                id="brand-offers-admin-pass"
                name="password"
                placeholder="••••••••"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                aria-label={
                  showPassword ? 'Hide password' : 'Show password as plain text'
                }
                className={styles.passwordToggle}
                disabled={isSubmitting}
                type="button"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? (
                  <RiEyeOffLine size={20} />
                ) : (
                  <RiEyeLine size={20} />
                )}
              </button>
            </span>
          </label>

          {error && <p className={styles.error}>{error}</p>}

          <button
            className={styles.button}
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
